import React from 'react';
import { List } from 'reactstrap';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import { toasty, Input } from '../../common/forms/FormElements';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { BlockBorder } from '../../common/Block';
import { SaveButton } from '../../common/forms/SaveButton';

/**
 * Shared class component for adding a new job city.
 * Can be used by both Admin Tools and Support Tools pages.
 *
 * Props:
 * @param {function} onSuccess - Callback after successful submission (e.g., refreshUsageHistory)
 */
class AddJobCityForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            city: '',
            state: '',
            selectedStateOption: null,
            usStates: [],
        };
    }

    componentDidMount() {
        this.loadUSStates();
    }

    loadUSStates = async () => {
        try {
            const usStates = await httpClient.js(ApiRoutes.typeAheads.USStates());
            this.setState({ usStates });
        } catch (err) {
            console.error(err);
        }
    };

    onUSStateChanged = (selectedOption) => {
        this.setState({
            state: selectedOption ? selectedOption.value : '',
            selectedStateOption: selectedOption,
        });
    };

    onSubmit = async (e) => {
        if (e) e.preventDefault();

        const { city, state } = this.state;

        if (!city) {
            toasty.error('Please enter a city.');
            return;
        }

        if (!state) {
            toasty.error('Please select a state.');
            return;
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.adminTool.addJobCity(), { city, state });

            this.setState({
                city: '',
                state: '',
                selectedStateOption: null,
            });

            toasty.success(`New City ${city} Added`);

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } catch (err) {
            console.error(err);
            toasty.error('Error: Add New City Failed. Please Check Form Inputs');
            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const {
            saving, city, usStates, selectedStateOption,
        } = this.state;
        const isSubmitDisabled = saving || !city.trim() || !selectedStateOption;

        return (
            <form onSubmit={this.onSubmit}>
                <Input
                    label="City"
                    id="city"
                    name="city"
                    placeholder="City"
                    onChange={(e) => this.setState({ city: e.target.value })}
                    value={city}
                    required
                    error="City is Required."
                />

                <ValidatedSelect
                    id="state"
                    label="State"
                    name="state"
                    required
                    options={usStates}
                    value={selectedStateOption}
                    onChange={this.onUSStateChanged}
                    error="A State selection is required."
                />

                <BlockBorder />

                <div className="d-flex justify-content-center">
                    <SaveButton
                        saving={saving}
                        disabled={isSubmitDisabled}
                        label="Submit"
                    />
                </div>
            </form>
        );
    }
}

/**
 * Renders the execution history details for AddJobCity.
 * Can be used by both Admin and SupportTools ExecutionHistory components.
 */
export const AddJobCityHistoryDetails = ({ record }) => (
    <List type="unstyled">
        {record.input.inputCity && (
            <li>
                <strong>City:</strong>
                {' '}
                {record.input.inputCity}
            </li>
        )}
        {record.input.inputState && (
            <li>
                <strong>State:</strong>
                {' '}
                {record.input.inputState}
            </li>
        )}
        {record.output?.message && (
            <li>
                <strong>Message:</strong>
                {' '}
                {record.output.message}
            </li>
        )}
    </List>
);

export { AddJobCityForm };
export default AddJobCityForm;
